import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Box, Flex, Heading, IconButton, Spinner } from '@radix-ui/themes';
import { DialogButton } from 'components/common/dialogs/button';
import { GlobalContext } from 'contexts/global.context';
import { t } from 'i18next';
import { IFullDialog } from 'interfaces/i-dialogs';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useState } from 'react';

const DEBUG_BORDERS = false;

export const CommonDialog = (props: IFullDialog) => {
  const { countDialog } = useContext(GlobalContext);

  const [open, setOpen] = useState(true);

  // track open dialog(s) in global context
  useEffect(() => {
    if (props.ignore) {
      return;
    }

    countDialog({
      id: props.identifier,
      open: true,
    });

    return () =>
      countDialog({
        id: props.identifier,
        open: false,
      });
  }, []);

  // trigger parent's onClose fn
  useEffect(() => {
    if (open) {
      return;
    }

    props.onClose();
  }, [open]);

  const isFlexing = !!props.vFlexHeight;

  const hasFooter =
    props.leftFooter ||
    (props.buttons ?? []).filter((b) => !b.invisible).length > 0;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => setOpen(value)}
      // allows interaction with other elements on the screen while open (e.g. toasts on the side)
      modal={false}
    >
      <div
        className="DialogOverlay"
        onClick={() => {
          if (props.modal) {
            // while in modal mode (default), clicking on the overlay closes the dialog
            return;
          }

          const IS_TOUCH_DEVICE = navigator.maxTouchPoints > 0;
          if (IS_TOUCH_DEVICE) {
            // prevents interactions with inputs in the dialog from accidentally closing things
            return;
          }

          props.onClose();
        }}
      ></div>

      {props.description && (
        <Dialog.Description>{props.description}</Dialog.Description>
      )}

      <Dialog.Content
        aria-describedby={props.description}
        className={['DialogContent', isFlexing ? RADIX.VFLEX.COLUMN : ''].join(
          ' '
        )}
        style={{
          // force width to match max
          width: props.width,
          maxWidth: RADIX.DIALOG.WIDTH.MAX,
          minWidth: RADIX.DIALOG.WIDTH.MIN,

          height: props.vFlexHeight,
          maxHeight: RADIX.DIALOG.HEIGHT.MAX,
          minHeight: RADIX.DIALOG.HEIGHT.MIN,

          overflowX: 'hidden',
          overflowY: 'auto',
          border: DEBUG_BORDERS ? '1px solid red' : undefined,
        }}
        // avoid dismissing when clicking on toasts
        onInteractOutside={(e) => e.preventDefault()}
      >
        <Flex
          direction="column"
          gap="5"
          style={{
            height: isFlexing ? '100%' : undefined,
            border: DEBUG_BORDERS ? '1px solid green' : undefined,
          }}
        >
          <Dialog.Title className="DialogTitle">
            <Flex pt={RADIX.FLEX.PAD.XS} justify="between">
              <Flex gap={RADIX.FLEX.GAP.SM} minWidth="0">
                <Box minWidth="0">
                  {typeof props.title === 'string' ? (
                    <Heading
                      size={RADIX.HEADING.SIZE.MODAL_TITLE}
                      mb="0"
                      truncate
                    >
                      {t(props.title)}
                    </Heading>
                  ) : (
                    props.title
                  )}

                  {props.subtitle}
                </Box>

                {props.loading && (
                  <Box className="valign-center" pb="1">
                    <Spinner size="3" />
                  </Box>
                )}
              </Flex>

              <IconButton
                color={RADIX.COLOR.NEUTRAL}
                variant="soft"
                className="btn-floating"
                onClick={props.onClose}
              >
                <Cross2Icon />
              </IconButton>
            </Flex>
          </Dialog.Title>

          <Box
            flexGrow={isFlexing ? '1' : undefined}
            overflowX={isFlexing ? 'hidden' : undefined}
            overflowY={isFlexing ? 'auto' : undefined}
            style={{
              border: DEBUG_BORDERS ? '1px solid yellow' : undefined,
            }}
          >
            {props.content}
          </Box>

          {hasFooter && (
            <Flex gap={RADIX.FLEX.GAP.SM} justify="between">
              <Box>{props.leftFooter}</Box>

              <Flex gap={RADIX.FLEX.GAP.SM}>
                {props.buttons
                  ?.filter((b) => !b.invisible)
                  .map((b, i) => <DialogButton key={i} {...b} />)}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
